import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoArrowBackCircleOutline } from 'react-icons/io5';
import LogoWhite from '../assets/LOGO_WHITE.png';
import Footer from '../components/Footer';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';

const BG_01 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_01.jpg?alt=media&token=9e891581-27bf-4bdd-83cf-a39b44af9324";
const BG_02 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_02.jpg?alt=media&token=b4394dc2-756e-41e8-9cfd-53c25137835d";
const BG_03 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_03.jpg?alt=media&token=989909db-898b-4f93-babf-0262e968ddff";
const BG_04 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_04.jpg?alt=media&token=b895a70e-d345-48b9-aa06-a359057f059a";
const BG_05 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_05.jpg?alt=media&token=d91d94d3-064a-4efa-8bcf-f37cd4a04714";

const MODEL_URL = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/app%2Fassets%2Flow-poly_city_buildings-optimized.glb?alt=media&token=aacc950c-90b5-44fa-ad0e-d1e38d45d151";
const SOFA_MODEL_URL = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/app%2Fassets%2Flow_poly_stylized_sofa.glb?alt=media&token=2e7b4cf5-4f06-4d0d-abe0-d40db2fa2da2";

// Debug mode toggle - set to false to disable debug overlay
const DEBUG_MODE = false;
const VERBOSE_DEBUG = false; // Set to true for extremely detailed logs

export default function ResidentialExperience() {
  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeGrid, setActiveGrid] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [bgIndex, setBgIndex] = useState(0);
  const [bgPosition, setBgPosition] = useState({ x: 0, y: 0 });
  const [bgScale, setBgScale] = useState(1);
  const [bgOpacity, setBgOpacity] = useState(0);
  const [gridOpacity, setGridOpacity] = useState(0.3);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [infoVisible, setInfoVisible] = useState({ amenity: false, service: false });
  const [selectedCard, setSelectedCard] = useState(null); // 'amenity' | 'service' | null
  const [hoveredCard, setHoveredCard] = useState(null);

  // Model fade‑in state
  const [modelReady, setModelReady] = useState(false);
  const [modelVisible, setModelVisible] = useState(false);
  
  // Model toggling state
  const [currentModel, setCurrentModel] = useState('city'); // 'city' | 'sofa'
  const [modelOpacity, setModelOpacity] = useState(1); // for smooth fade transitions
  const modelToggleIntervalRef = useRef(null);

  // Debug state
  const [debugLogs, setDebugLogs] = useState([]);
  const [debugVisible, setDebugVisible] = useState(DEBUG_MODE);

  const lastGridChangeTime = useRef(0);
  const bgIntervalRef = useRef(null);

  // 3D model refs
  const threeMountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const modelRef = useRef(null);
  const sofaModelRef = useRef(null);
  const animationFrameRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const rotationStartTimeRef = useRef(null);
  const sofaBaseRotationRef = useRef(0); // base rotation offset for the sofa

  // Grid animation refs (direct DOM manipulation)
  const gridContainerRef = useRef(null);
  const scrollXRef = useRef(0);
  const scrollYRef = useRef(0);
  const activeGridRef = useRef(0);

  // Swipe refs for touch navigation
  const touchStartXRef = useRef(0);
  const touchEndXRef = useRef(0);
  const swipeThreshold = 50;

  // ---- HEADING REVEAL STATE (runs once) ----
  const [headingReveal, setHeadingReveal] = useState(false);

  const backgrounds = [BG_01, BG_02, BG_03, BG_04, BG_05];

  // Debug log function
  const addDebugLog = (message, type = 'log') => {
    const timestamp = new Date().toLocaleTimeString();
    const icon = type === 'error' ? '❌' : type === 'warn' ? '⚠️' : '📘';
    setDebugLogs(prev => [...prev, { timestamp, message, type, icon }]);
  };

  // Override console methods to capture logs (only in debug mode)
  useEffect(() => {
    if (!DEBUG_MODE) return;

    const originalConsoleLog = console.log;
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;

    console.log = function(...args) {
      originalConsoleLog.apply(console, args);
      addDebugLog(args.map(arg =>
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' '), 'log');
    };

    console.error = function(...args) {
      originalConsoleError.apply(console, args);
      addDebugLog(args.map(arg =>
        arg instanceof Error ? `${arg.message}\n${arg.stack || ''}` :
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' '), 'error');
    };

    console.warn = function(...args) {
      originalConsoleWarn.apply(console, args);
      addDebugLog(args.map(arg =>
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' '), 'warn');
    };

    return () => {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
    };
  }, []);

  // Copy debug logs to clipboard
  const copyLogsToClipboard = () => {
    const logText = debugLogs.map(log =>
      `[${log.timestamp}] ${log.icon} ${log.type.toUpperCase()}: ${log.message}`
    ).join('\n');

    navigator.clipboard.writeText(logText).then(() => {
      addDebugLog('✅ Debug logs copied to clipboard!', 'log');
    }).catch(err => {
      addDebugLog('❌ Failed to copy logs: ' + err.message, 'error');
    });
  };

  // Clear debug logs
  const clearDebugLogs = () => {
    setDebugLogs([]);
  };

  useEffect(() => {
    document.title = "Residential Experience | Outlne";
    if (DEBUG_MODE) {
      addDebugLog('=== COMPONENT MOUNTED ===', 'log');
    }
  }, []);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1224);
      setWindowWidth(width);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
      setBgOpacity(0);
      // Trigger handwriting reveal after a short delay
      setTimeout(() => {
        setHeadingReveal(true);
      }, 300);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Background slideshow
  useEffect(() => {
    if (!pageLoaded) return;

    backgrounds.forEach(src => {
      const img = new Image();
      img.src = src;
    });

    const changeBackground = () => {
      setBgIndex(prev => (prev + 1) % backgrounds.length);
      const direction = Math.random() > 0.5 ? 1 : -1;
      const axis = Math.random() > 0.5 ? 'x' : 'y';

      setBgPosition({ x: 0, y: 0 });
      setBgScale(1.05);

      setTimeout(() => {
        setBgPosition({
          x: axis === 'x' ? direction * 5 : 0,
          y: axis === 'y' ? direction * 5 : 0
        });
      }, 10);
    };

    changeBackground();
    bgIntervalRef.current = setInterval(changeBackground, 8000);

    return () => clearInterval(bgIntervalRef.current);
  }, [pageLoaded]);

  // Grid animation – uses refs directly, zero React re-renders
  useEffect(() => {
    if (!pageLoaded) return;

    let animationFrame;
    let lastTime = 0;
    const scrollSpeed = 10;

    const animateScroll = (time) => {
      if (!lastTime) lastTime = time;
      const delta = (time - lastTime) / 1000;
      lastTime = time;

      const timeSinceChange = time - lastGridChangeTime.current;

      if (timeSinceChange > 100) {
        scrollXRef.current = (scrollXRef.current + scrollSpeed * delta) % 80;
        scrollYRef.current = (scrollYRef.current + scrollSpeed * delta) % 80;
        if (gridContainerRef.current) {
          gridContainerRef.current.style.transform =
            `translate3d(-${scrollXRef.current}px, -${scrollYRef.current}px, 0)`;
        }
      }

      animationFrame = requestAnimationFrame(animateScroll);
    };

    animationFrame = requestAnimationFrame(animateScroll);

    const interval = setInterval(() => {
      lastGridChangeTime.current = performance.now();
      scrollXRef.current = 0;
      scrollYRef.current = 0;
      if (gridContainerRef.current) {
        gridContainerRef.current.style.transform = 'translate3d(0px, 0px, 0)';
      }
      setActiveGrid(prev => {
        const next = (prev + 1) % 3;
        activeGridRef.current = next;
        return next;
      });
    }, 4000);

    return () => {
      cancelAnimationFrame(animationFrame);
      clearInterval(interval);
    };
  }, [pageLoaded]);

  // ============================================================
  // 3D Model Background (Three.js)
  // ============================================================
  useEffect(() => {
    THREE.Cache.enabled = true;

    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) {
      if (DEBUG_MODE) addDebugLog('❌ CRITICAL: WebGL not supported!', 'error');
      return;
    }

    const scene = new THREE.Scene();
    scene.background = null;

    const camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.set(0, 5, 25);
    camera.lookAt(0, -3, 0);

    const isMobileDevice = window.innerWidth < 768;
    const isTabletDevice = window.innerWidth >= 768 && window.innerWidth < 1224;

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    const pixelRatio = isMobileDevice ? Math.min(window.devicePixelRatio, 1.5) : Math.min(window.devicePixelRatio, 2);
    renderer.setPixelRatio(pixelRatio);
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    if (threeMountRef.current) {
      threeMountRef.current.appendChild(renderer.domElement);
      renderer.domElement.style.pointerEvents = 'none';
      renderer.domElement.style.position = 'absolute';
      renderer.domElement.style.top = '0';
      renderer.domElement.style.left = '0';
      renderer.domElement.style.width = '100%';
      renderer.domElement.style.height = '100%';
      renderer.domElement.style.opacity = '0';
      renderer.domElement.style.transition = 'opacity 1.5s ease';
      renderer.domElement.style.willChange = 'opacity, transform';
    }

    const ambientLight = new THREE.AmbientLight(0x404040, 0.5);
    scene.add(ambientLight);

    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight1.position.set(1, 1, 1);
    scene.add(directionalLight1);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
    directionalLight2.position.set(-1, -0.5, -0.5);
    scene.add(directionalLight2);

    const DESKTOP_SCALE = 4.5;
    const TABLET_SCALE = 3.8;
    const MOBILE_SCALE = 2.8;

    // ⚠️ SOFA SCALE ADJUSTMENT AREA – modify these values to scale the sofa up/down
    const SOFA_DESKTOP_SCALE = 10.0;  // <-- Adjust sofa desktop scale here
    const SOFA_TABLET_SCALE = 8.5;    // <-- Adjust sofa tablet scale here
    const SOFA_MOBILE_SCALE = 6.5;    // <-- Adjust sofa mobile scale here

    const wireframeMat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(0xffffff),
      wireframe: true,
      transparent: true,
      opacity: 0.25,
    });

    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
    dracoLoader.setDecoderConfig({ type: 'js' });

    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    let modelScale;
    if (isMobileDevice) modelScale = MOBILE_SCALE;
    else if (isTabletDevice) modelScale = TABLET_SCALE;
    else modelScale = DESKTOP_SCALE;

    const POSITION_Y_DESKTOP = -8;
    const POSITION_Y_TABLET = -7;
    const POSITION_Y_MOBILE = -5;
    const POSITION_Z_DESKTOP = 0;
    const POSITION_Z_TABLET = 0;
    const POSITION_Z_MOBILE = 0;
    let positionY, positionZ;
    if (isMobileDevice) {
      positionY = POSITION_Y_MOBILE;
      positionZ = POSITION_Z_MOBILE;
    } else if (isTabletDevice) {
      positionY = POSITION_Y_TABLET;
      positionZ = POSITION_Z_TABLET;
    } else {
      positionY = POSITION_Y_DESKTOP;
      positionZ = POSITION_Z_DESKTOP;
    }

    // ⚠️ SOFA POSITION ADJUSTMENT AREA – modify these values to reposition the sofa
    const SOFA_POSITION_Y_DESKTOP = -5;   // <-- Adjust sofa Y position (desktop)
    const SOFA_POSITION_Y_TABLET = -5;    // <-- Adjust sofa Y position (tablet)
    const SOFA_POSITION_Y_MOBILE = -4;    // <-- Adjust sofa Y position (mobile)
    const SOFA_POSITION_Z_DESKTOP = 0;    // <-- Adjust sofa Z position (desktop)
    const SOFA_POSITION_Z_TABLET = 0;     // <-- Adjust sofa Z position (tablet)
    const SOFA_POSITION_Z_MOBILE = 0;     // <-- Adjust sofa Z position (mobile)
    let sofaPositionY, sofaPositionZ;
    if (isMobileDevice) {
      sofaPositionY = SOFA_POSITION_Y_MOBILE;
      sofaPositionZ = SOFA_POSITION_Z_MOBILE;
    } else if (isTabletDevice) {
      sofaPositionY = SOFA_POSITION_Y_TABLET;
      sofaPositionZ = SOFA_POSITION_Z_TABLET;
    } else {
      sofaPositionY = SOFA_POSITION_Y_DESKTOP;
      sofaPositionZ = SOFA_POSITION_Z_DESKTOP;
    }
    
    let sofaModelScale;
    if (isMobileDevice) sofaModelScale = SOFA_MOBILE_SCALE;
    else if (isTabletDevice) sofaModelScale = SOFA_TABLET_SCALE;
    else sofaModelScale = SOFA_DESKTOP_SCALE;

    // ---- Load City Model ----
    const xhr = new XMLHttpRequest();
    xhr.open('GET', MODEL_URL, true);
    xhr.responseType = 'arraybuffer';
    xhr.timeout = 30000;

    xhr.onload = () => {
      if (xhr.status === 200) {
        const blob = new Blob([xhr.response], { type: 'model/gltf-binary' });
        const blobUrl = URL.createObjectURL(blob);

        loader.load(
          blobUrl,
          (gltf) => {
            const model = gltf.scene;
            modelRef.current = model;
            rotationStartTimeRef.current = performance.now();

            model.traverse((child) => {
              if (child.isMesh) {
                child.material = wireframeMat.clone();
              }
            });

            const box = new THREE.Box3().setFromObject(model);
            const center = box.getCenter(new THREE.Vector3());
            model.position.set(-center.x, -center.y + positionY, -center.z + positionZ);
            model.scale.set(modelScale, modelScale, modelScale);
            scene.add(model);
            URL.revokeObjectURL(blobUrl);

            setModelReady(true);
          },
          undefined,
          (error) => {
            if (DEBUG_MODE) addDebugLog('❌ GLTF parsing from blob failed (city)', 'error');
            URL.revokeObjectURL(blobUrl);
          }
        );
      }
    };

    xhr.onerror = () => {
      if (DEBUG_MODE) addDebugLog('❌ XHR network error (city)', 'error');
    };

    xhr.send();

    // ---- Load Sofa Model ----
    const sofaXhr = new XMLHttpRequest();
    sofaXhr.open('GET', SOFA_MODEL_URL, true);
    sofaXhr.responseType = 'arraybuffer';
    sofaXhr.timeout = 30000;

    sofaXhr.onload = () => {
      if (sofaXhr.status === 200) {
        const blob = new Blob([sofaXhr.response], { type: 'model/gltf-binary' });
        const blobUrl = URL.createObjectURL(blob);

        loader.load(
          blobUrl,
          (gltf) => {
            const sofaModel = gltf.scene;
            sofaModelRef.current = sofaModel;

            sofaModel.traverse((child) => {
              if (child.isMesh) {
                child.material = wireframeMat.clone();
              }
            });

            const box = new THREE.Box3().setFromObject(sofaModel);
            const center = box.getCenter(new THREE.Vector3());
            sofaModel.position.set(-center.x, -center.y + sofaPositionY, -center.z + sofaPositionZ);
            sofaModel.scale.set(sofaModelScale, sofaModelScale, sofaModelScale);
            // ⚠️ SOFA INITIAL ROTATION – adjust to face the desired direction on first appearance
            sofaModel.rotation.y = Math.PI * 0.75; // Try 0, Math.PI/2, Math.PI, Math.PI*1.5
            sofaBaseRotationRef.current = sofaModel.rotation.y; // remember the offset
            // Start hidden (will be shown by toggle logic)
            sofaModel.visible = false;
            scene.add(sofaModel);
            URL.revokeObjectURL(blobUrl);
          },
          undefined,
          (error) => {
            if (DEBUG_MODE) addDebugLog('❌ GLTF parsing from blob failed (sofa)', 'error');
            URL.revokeObjectURL(blobUrl);
          }
        );
      }
    };

    sofaXhr.onerror = () => {
      if (DEBUG_MODE) addDebugLog('❌ XHR network error (sofa)', 'error');
    };

    sofaXhr.send();

    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      const elapsed = rotationStartTimeRef.current
        ? (performance.now() - rotationStartTimeRef.current) / 1000
        : 0;

      // City model – base rotation 0
      if (modelRef.current) {
        modelRef.current.rotation.y = (elapsed * 0.3) % (Math.PI * 2);
      }
      // Sofa model – uses its custom base rotation
      if (sofaModelRef.current) {
        sofaModelRef.current.rotation.y = ((elapsed * 0.3) + sofaBaseRotationRef.current) % (Math.PI * 2);
      }

      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = setTimeout(() => {
        if (camera && renderer) {
          camera.aspect = window.innerWidth / window.innerHeight;
          camera.updateProjectionMatrix();
          renderer.setSize(window.innerWidth, window.innerHeight);
        }
      }, 250);
    };
    window.addEventListener('resize', handleResize);

    sceneRef.current = scene;
    rendererRef.current = renderer;
    cameraRef.current = camera;

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (rendererRef.current) {
        rendererRef.current.dispose();
        if (threeMountRef.current && rendererRef.current.domElement) {
          threeMountRef.current.removeChild(rendererRef.current.domElement);
        }
      }
      if (sceneRef.current) {
        sceneRef.current.traverse((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
        sceneRef.current.clear();
      }
    };
  }, []);

  // -----------------------------------------------------------
  // Fade‑in sequence after model is fully loaded
  // -----------------------------------------------------------
  useEffect(() => {
    if (!modelReady) return;
    setModelVisible(true);
  }, [modelReady]);

  useEffect(() => {
    if (!rendererRef.current) return;
    rendererRef.current.domElement.style.opacity = modelVisible ? '1' : '0';
  }, [modelVisible]);

  // -----------------------------------------------------------
  // Model Toggle Logic – switches between city and sofa every 5 seconds
  // -----------------------------------------------------------
  const fadeOutModel = (callback) => {
    setModelOpacity(0);
    setTimeout(() => {
      if (callback) callback();
    }, 1000); // 1 second fade-out
  };

  const fadeInModel = () => {
    setTimeout(() => {
      setModelOpacity(1);
    }, 50); // small delay to ensure the switch happens first
  };

  useEffect(() => {
    if (!modelReady) return;
    // Wait until sofa model is also loaded before starting toggle
    if (!sofaModelRef.current) return;

    const toggleModel = () => {
      fadeOutModel(() => {
        setCurrentModel(prev => {
          const next = prev === 'city' ? 'sofa' : 'city';
          // Update visibility in Three.js scene
          if (modelRef.current) modelRef.current.visible = next === 'city';
          if (sofaModelRef.current) sofaModelRef.current.visible = next === 'sofa';
          return next;
        });
        fadeInModel();
      });
    };

    // Initial toggle after 5 seconds
    const initialTimer = setTimeout(() => {
      toggleModel();
      // Then set interval for every 5 seconds after the first toggle
      modelToggleIntervalRef.current = setInterval(toggleModel, 5000);
    }, 5000);

    return () => {
      clearTimeout(initialTimer);
      if (modelToggleIntervalRef.current) clearInterval(modelToggleIntervalRef.current);
    };
  }, [modelReady]);

  // Apply model opacity to the renderer's canvas
  useEffect(() => {
    if (!rendererRef.current) return;
    rendererRef.current.domElement.style.opacity = modelOpacity;
    rendererRef.current.domElement.style.transition = 'opacity 1s ease';
  }, [modelOpacity]);

  // Static grid styles
  const gridStyles = [
    {
      backgroundSize: '40px 40px',
      display: activeGrid === 0 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      backgroundSize: '50px 40px',
      backgroundPositionX: '20px',
      display: activeGrid === 1 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease',
      transform: 'skewX(-15deg)'
    },
    {
      backgroundSize: '40px 30px',
      backgroundPositionY: '10px',
      display: activeGrid === 2 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease',
      transform: 'perspective(500px) rotateX(15deg)'
    }
  ];

  // Dot click handlers
  const handleAmenityDot = (index) => {
    setInfoVisible({
      amenity: index === 1,
      service: false,
    });
    setSelectedCard(index === 1 ? 'amenity' : null);
  };

  const handleServiceDot = (index) => {
    setInfoVisible({
      service: index === 1,
      amenity: false,
    });
    setSelectedCard(index === 1 ? 'service' : null);
  };

  // Swipe handlers
  const handleAmenityTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleAmenityTouchEnd = (e) => {
    touchEndXRef.current = e.changedTouches[0].clientX;
    const diff = touchStartXRef.current - touchEndXRef.current;
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0 && !infoVisible.amenity) {
        setInfoVisible({ amenity: true, service: false });
        setSelectedCard('amenity');
      }
      else if (diff < 0 && infoVisible.amenity) {
        setInfoVisible({ amenity: false, service: false });
        setSelectedCard(null);
      }
    }
  };

  const handleServiceTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleServiceTouchEnd = (e) => {
    touchEndXRef.current = e.changedTouches[0].clientX;
    const diff = touchStartXRef.current - touchEndXRef.current;
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0 && !infoVisible.service) {
        setInfoVisible({ service: true, amenity: false });
        setSelectedCard('service');
      }
      else if (diff < 0 && infoVisible.service) {
        setInfoVisible({ service: false, amenity: false });
        setSelectedCard(null);
      }
    }
  };

  // Handwriting reveal – animation runs exactly once, then characters stay visible
  const [headingAnimationComplete, setHeadingAnimationComplete] = useState(false);
  const [headingHalfComplete, setHeadingHalfComplete] = useState(false);
  const [subtitleVisible, setSubtitleVisible] = useState(false);

  useEffect(() => {
    if (headingReveal && !headingAnimationComplete) {
      const totalAnimationTime = 2500; // ms – covers ~40 characters at 0.05s each + 0.6s keyframe
      const timer = setTimeout(() => {
        setHeadingAnimationComplete(true);
      }, totalAnimationTime);
      return () => clearTimeout(timer);
    }
  }, [headingReveal, headingAnimationComplete]);

  useEffect(() => {
    if (headingReveal && !headingHalfComplete) {
      const timer = setTimeout(() => {
        setHeadingHalfComplete(true);
      }, 1250); // ~50% of total heading animation
      return () => clearTimeout(timer);
    }
  }, [headingReveal, headingHalfComplete]);

  useEffect(() => {
    if (headingHalfComplete && !subtitleVisible) {
      const timer = setTimeout(() => setSubtitleVisible(true), 10);
      return () => clearTimeout(timer);
    }
  }, [headingHalfComplete, subtitleVisible]);

  const shouldAnimate = headingReveal && !headingAnimationComplete;



  return (
    <>
      {/* Fixed Background Layer */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(29, 29, 29, 0.8)',
        zIndex: -3,
      }} />

      {/* Background Slideshow */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -2,
        overflow: 'hidden',
        opacity: bgOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          right: '-10%',
          bottom: '-10%',
          backgroundImage: `url(${backgrounds[bgIndex]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transform: `scale(${bgScale}) translate(${bgPosition.x}%, ${bgPosition.y}%)`,
          mixBlendMode: 'soft-light',
          transition: 'background-image 1s ease-in-out, transform 8s linear',
        }} />
      </div>

      {/* Grid Container */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: isMobile ? '100vw' : '50vw',
        bottom: 0,
        zIndex: -1,
        overflow: 'hidden',
        opacity: gridOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div
          ref={gridContainerRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: isMobile ? '100vw' : '200vw',
            maskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
            WebkitMaskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
            willChange: 'transform',
            transform: 'translate3d(0px, 0px, 0)' // stable initial value
          }}
        >
          {gridStyles.map((style, index) => (
            <div key={index} style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                linear-gradient(to right, #a0a0a0 1px, transparent 1px),
                linear-gradient(to bottom, #a0a0a0 1px, transparent 1px)
              `,
              ...style
            }} />
          ))}
        </div>
      </div>

      {/* 3D MODEL CONTAINER */}
      <div
        ref={threeMountRef}
        style={{
          position: 'fixed',
          top: isMobile ? '-85px' : '-100px',
          left: isMobile ? '53%' : '50%',
          transform: 'translateX(-50%)',
          width: isMobile ? '120vw' : '100vw',
          height: isMobile ? '100vh' : '120vh',
          zIndex: 1,
          opacity: 0.6,
          pointerEvents: 'none',
          overflow: 'visible',
        }}
      >
        {/* Top fade */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '30%',
          background: 'linear-gradient(to bottom, rgba(26,26,26,1) 0%, rgba(26,26,26,0.8) 40%, rgba(26,26,26,0) 100%)',
          pointerEvents: 'none',
          zIndex: 2,
        }} />

        {/* Bottom fade */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '50%',
          background: 'linear-gradient(to top, rgba(26,26,26,1) 0%, rgba(26,26,26,0.9) 40%, rgba(26,26,26,0.4) 70%, rgba(26,26,26,0) 100%)',
          pointerEvents: 'none',
          zIndex: 2,
        }} />
      </div>

      {/* Debug overlay (disabled when DEBUG_MODE=false) */}
      {DEBUG_MODE && debugVisible && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '40%',
          backgroundColor: 'rgba(26, 26, 26, 0.95)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          borderTop: '2px solid #40a3ff',
          fontFamily: 'monospace',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            backgroundColor: '#2a2a2a',
            borderBottom: '1px solid #444',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#40a3ff', fontWeight: 'bold', fontSize: '14px' }}>🔍 DEBUG CONSOLE</span>
              <span style={{ backgroundColor: '#40a3ff', color: '#1a1a1a', padding: '2px 6px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' }}>
                {debugLogs.length} logs
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={copyLogsToClipboard} style={{ backgroundColor: '#40a3ff', border: 'none', color: '#1a1a1a', padding: '4px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>📋 Copy</button>
              <button onClick={clearDebugLogs} style={{ backgroundColor: '#666666', border: 'none', color: 'white', padding: '4px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>🗑️ Clear</button>
              <button onClick={() => setDebugVisible(false)} style={{ backgroundColor: '#444', border: 'none', color: 'white', padding: '4px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>✕ Close</button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px', fontSize: '11px', lineHeight: '1.4' }}>
            {debugLogs.map((log, index) => (
              <div key={index} style={{ padding: '4px 6px', marginBottom: '2px', backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent', borderRadius: '3px', wordBreak: 'break-word' }}>
                <span style={{ color: '#888', marginRight: '6px' }}>{log.timestamp}</span>
                <span style={{ marginRight: '6px' }}>{log.icon}</span>
                <span style={{ color: log.type === 'error' ? '#ff4444' : log.type === 'warn' ? '#ffaa00' : '#40a3ff' }}>{log.type.toUpperCase()}:</span>
                <span style={{ color: '#ccc', marginLeft: '6px' }}>{log.message}</span>
              </div>
            ))}
          </div>
          <div style={{ padding: '6px 12px', backgroundColor: '#2a2a2a', borderTop: '1px solid #444', display: 'flex', gap: '16px', fontSize: '10px', color: '#888' }}>
            <span>📱 {windowWidth}px | {isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop'}</span>
            <span>🖥️ {window.devicePixelRatio}x DPR</span>
          </div>
        </div>
      )}

      {DEBUG_MODE && isMobile && !debugVisible && (
        <button
          onClick={() => setDebugVisible(true)}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 9998,
            backgroundColor: 'rgba(26, 26, 26, 0.8)',
            border: '1px solid #40a3ff',
            color: '#40a3ff',
            width: '44px',
            height: '44px',
            borderRadius: '22px',
            fontSize: '20px',
            cursor: 'pointer',
            backdropFilter: 'blur(4px)',
          }}
        >
          🔍
        </button>
      )}

      {/* Desktop Back Button */}
      {!isMobile && (
        <div
          onClick={() => navigate('/pricing')}
          style={{
            position: 'fixed',
            top: '30px',
            left: '30px',
            zIndex: 20,
            cursor: 'pointer',
            fontSize: '2rem',
            color: 'white',
          }}
        >
          <IoArrowBackCircleOutline />
        </div>
      )}

      {/* Desktop Header */}
      {!isMobile && (
        <div style={{ position: 'fixed', top: '40px', right: '40px', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src={LogoWhite} alt="Outlne Logo" style={{ width: '40px', height: 'auto' }} />
            <h1 style={{ fontSize: '1.8rem', fontFamily: 'avenir-lt-pro, sans-serif', letterSpacing: '-1.2px', margin: 0, color: 'white', fontWeight: 500 }}>Outlne</h1>
          </div>
          <p style={{ fontSize: '1rem', margin: '5px 0 0 0', color: 'white', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '500' }}>
            Interior Design <span style={{ backgroundColor: '#ffffff', color: 'black', padding: '2px 8px', borderRadius: '4px', display: 'inline-block', minWidth: '60px', textAlign: 'center' }}>Editor</span>
          </p>
        </div>
      )}

      {/* Mobile Header */}
      {isMobile && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '110px',
            backgroundColor: 'rgba(31, 31, 31, 0.8)',
            backdropFilter: 'blur(10px)',
            zIndex: 15,
          }} />
          <div style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 20,
            textAlign: 'left',
          }}>
            <h1 style={{ fontSize: '1.5rem', fontFamily: 'avenir-lt-pro, sans-serif', letterSpacing: '-1.2px', margin: 0, color: 'white', fontWeight: 500 }}>Outlne</h1>
            <p style={{ fontSize: '0.9rem', margin: '5px 0 0 0', color: 'white', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '500' }}>
              Interior Design <span style={{ backgroundColor: '#ffffff', color: 'black', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', minWidth: '50px', textAlign: 'center' }}>Editor</span>
            </p>
          </div>
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '10px',
          }}>
            <img src={LogoWhite} alt="Outlne Logo" style={{ width: '40px', height: '40px' }} />
            <div onClick={() => navigate('/pricing')} style={{ cursor: 'pointer', marginRight: '5px', fontSize: '1.8rem', color: 'white', marginTop: '2px' }}>
              <IoArrowBackCircleOutline />
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 3,
        overflowY: 'auto',
        overflowX: 'hidden',
        opacity: pageLoaded ? 1 : 0,
        transition: 'opacity 1s ease',
        overscrollBehavior: 'none',
        pointerEvents: 'auto',
        transform: 'translateZ(0)', // force GPU layer to prevent layout shift
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: '100vh',
          paddingTop: isMobile ? '135px' : '0px',
          paddingBottom: '0',
          position: 'relative',
          zIndex: 3,
        }}>
          {/* Page Heading + Subheading */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            maxWidth: isMobile ? 'calc(80% + 15px)' : '1000px',
            padding: isMobile ? '0 32px' : '0 40px',
            textAlign: 'center',
            color: 'white',
          }}>
            <h1
              style={{
                fontFamily: 'didot, sans-serif',
                fontStyle: 'italic',
                fontSize: isMobile ? '2.1rem' : isTablet ? '2.8rem' : '3.5rem',
                fontWeight: '400',
                width: '600px',
                marginBottom: '1.1rem',
                lineHeight: '1.1',
                letterSpacing: '-0.5px',
                color: 'white',
                whiteSpace: 'pre-line',
              }}
            >
              {["Built for the", "Residential Experience"].map((line, lineIndex) => (
                <span key={lineIndex} style={{ display: 'block' }}>
                  {line.split('').map((char, charIndex) => {
                    const previousChars = ["Built for the", "Residential Experience"]
                      .slice(0, lineIndex)
                      .reduce((sum, l) => sum + l.length, 0);
                    const delay = (previousChars + charIndex) * 0.05;
                    return (
                      <span
                        key={`${lineIndex}-${charIndex}`}
                        className={
                          shouldAnimate
                            ? 'hand-char-animate'
                            : headingReveal
                            ? 'hand-char-visible'
                            : 'hand-char-hidden'
                        }
                        style={{
                          display: 'inline-block',
                          animationDelay: `${delay}s`,
                          animationFillMode: 'both',
                          whiteSpace: char === ' ' ? 'pre' : 'normal',
                        }}
                      >
                        {char === ' ' ? '\u00A0' : char}
                      </span>
                    );
                  })}
                </span>
              ))}
            </h1>
            <p className={subtitleVisible ? 'subtitle-reveal' : 'subtitle-hidden'}
              style={{
              color: '#ffffffff',
              borderRadius: '5px',
              fontSize: isMobile ? '0.7rem' : '0.9rem',
              lineHeight: '1.5',
              fontFamily: 'avenir-lt-pro, sans-serif',
              maxWidth: '1200px',
              margin: '0 auto 3.5rem auto',
            }}>
              Are you looking to offer Outlne<br></br> as an Amenity or as a Service?
            </p>
          </div>

          {/* Cards section */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            maxWidth: '1400px',
            padding: isMobile ? '0 20px' : '0 40px',
          }}>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              marginTop: '-20px',
              justifyContent: 'center',
              gap: isMobile ? '30px' : '40px',
              width: '100%',
            }}>
              {/* Amenity Card */}
              <div onTouchStart={handleAmenityTouchStart} onTouchEnd={handleAmenityTouchEnd}
                onClick={() => setSelectedCard(selectedCard === 'amenity' ? null : 'amenity')}
                onMouseEnter={() => setHoveredCard('amenity')}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  border: selectedCard === 'amenity' ? '2px solid #40a3ff' : hoveredCard === 'amenity' ? '2px solid rgba(64, 163, 255, 0.7)' : '2px solid white',
                  borderRadius: '32px',
                  padding: isMobile ? '32px 44px' : '45px 52px',
                  width: isMobile ? '100%' : '480px',
                  minWidth: isMobile ? 'auto' : '340px',
                  maxWidth: isMobile ? '100%' : '520px',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(34, 34, 34, 0.9)',
                  minHeight: '282px',
                  touchAction: 'pan-y',
                  cursor: 'pointer',
                  transition: 'border-color 0.3s ease',
                }}
              >
                <div style={{ position: 'relative', width: '100%', height: '120px', marginBottom: '0px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.4s ease, transform 0.4s ease', opacity: infoVisible.amenity ? 0 : 1, transform: infoVisible.amenity ? 'scale(0.95)' : 'scale(1)', pointerEvents: infoVisible.amenity ? 'none' : 'auto' }}>
                    <h2 style={{ fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '2rem', fontWeight: '400', color: 'white', marginBottom: '10px', marginTop: '0' }}>Amenity</h2>
                    <p style={{ fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.95rem', color: '#ddd', textAlign: 'center', lineHeight: '1.5', whiteSpace: 'pre-line', margin: '0', minWidth: '200px' }}>
                      Designed for Luxury Buildings{'\n'}Easy Onboarding
                    </p>
                  </div>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.4s ease, transform 0.4s ease', opacity: infoVisible.amenity ? 1 : 0, transform: infoVisible.amenity ? 'scale(1)' : 'scale(0.95)', pointerEvents: infoVisible.amenity ? 'auto' : 'none' }}>
                    <p style={{ fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.85rem', color: '#ccc', textAlign: 'center', lineHeight: '1.5', margin: '0', minWidth: '200px' }}>
                      Elevate your building's prestige. Outlne is provided as a complimentary amenity.<br /> Tenants get seamless access to professional Interior Design tools.
                    </p>
                  </div>
                </div>
                {!infoVisible.amenity && (
                  <button onClick={(e) => { e.stopPropagation(); navigate('/residential/amenity'); }}
                    style={{ backgroundColor: '#454545ff', border: 'none', color: 'white', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1rem', fontWeight: '500', padding: '8px 54px', borderRadius: '30px', cursor: 'pointer', width: 'auto', marginTop: '16px' }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#666'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#454545ff'}
                  >
                    Enroll
                  </button>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                  <div onClick={(e) => { e.stopPropagation(); handleAmenityDot(0); }} style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: !infoVisible.amenity ? '#408aff' : '#555', cursor: 'pointer', transition: 'background-color 0.3s' }} />
                  <div onClick={(e) => { e.stopPropagation(); handleAmenityDot(1); }} style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: infoVisible.amenity ? '#408aff' : '#555', cursor: 'pointer', transition: 'background-color 0.3s' }} />
                </div>
              </div>

              {/* "or" Separator */}
              <span style={{ color: 'white', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1.2rem', fontWeight: '300' }}>or</span>

              {/* Service Card */}
              <div onTouchStart={handleServiceTouchStart} onTouchEnd={handleServiceTouchEnd}
                onClick={() => setSelectedCard(selectedCard === 'service' ? null : 'service')}
                onMouseEnter={() => setHoveredCard('service')}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  border: selectedCard === 'service' ? '2px solid #40a3ff' : hoveredCard === 'service' ? '2px solid rgba(64, 163, 255, 0.7)' : '2px solid white',
                  borderRadius: '32px',
                  padding: isMobile ? '32px 44px' : '45px 52px',
                  width: isMobile ? '100%' : '480px',
                  minWidth: isMobile ? 'auto' : '340px',
                  maxWidth: isMobile ? '100%' : '520px',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(34, 34, 34, 0.9)',
                  minHeight: '282px',
                  touchAction: 'pan-y',
                  cursor: 'pointer',
                  transition: 'border-color 0.3s ease',
                }}
              >
                <div style={{ position: 'relative', width: '100%', height: '120px', marginBottom: '0px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.4s ease, transform 0.4s ease', opacity: infoVisible.service ? 0 : 1, transform: infoVisible.service ? 'scale(0.95)' : 'scale(1)', pointerEvents: infoVisible.service ? 'none' : 'auto' }}>
                    <h2 style={{ fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '2rem', fontWeight: '400', color: 'white', marginBottom: '10px', marginTop: '0' }}>Service</h2>
                    <p style={{ fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.95rem', color: '#ddd', textAlign: 'center', lineHeight: '1.5', whiteSpace: 'pre-line', margin: '0', minWidth: '200px' }}>
                      Tenants Pay Separately{'\n'}Self-Managed Subscriptions
                    </p>
                  </div>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.4s ease, transform 0.4s ease', opacity: infoVisible.service ? 1 : 0, transform: infoVisible.service ? 'scale(1)' : 'scale(0.95)', pointerEvents: infoVisible.service ? 'auto' : 'none' }}>
                    <p style={{ fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.85rem', color: '#ccc', textAlign: 'center', lineHeight: '1.5', margin: '0', minWidth: '200px' }}>
                      Empower tenants with choice. Outlne is offered as an a la carte subscription.<br /> Tenants opt-in and pay directly, enjoying premium Interior Design features with simple, automated billing.
                    </p>
                  </div>
                </div>
                {!infoVisible.service && (
                  <button onClick={(e) => { e.stopPropagation(); navigate('/residential/service'); }}
                    style={{ backgroundColor: '#454545ff', border: 'none', color: 'white', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1rem', fontWeight: '500', padding: '8px 54px', borderRadius: '30px', cursor: 'pointer', width: 'auto', marginTop: '16px' }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#666'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#454545ff'}
                  >
                    Enroll
                  </button>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                  <div onClick={(e) => { e.stopPropagation(); handleServiceDot(0); }} style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: !infoVisible.service ? '#408aff' : '#555', cursor: 'pointer', transition: 'background-color 0.3s' }} />
                  <div onClick={(e) => { e.stopPropagation(); handleServiceDot(1); }} style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: infoVisible.service ? '#408aff' : '#555', cursor: 'pointer', transition: 'background-color 0.3s' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          width: '100%',
          position: 'relative',
          zIndex: 3,
          backgroundColor: '#2a2a2a',
          marginTop: isMobile ? '4rem' : '5rem',
        }}>
          <Footer />
        </div>
      </div>

      {/* Global styles + handwriting animation */}
      <style>{`
        html, body, #root {
          overflow: hidden;
          height: 100%;
          width: 100%;
          position: fixed;
          margin: 0;
          padding: 0;
        }
        * {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          -webkit-tap-highlight-color: transparent;
        }

        /* Handwriting reveal keyframes */
        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-4px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .hand-char-animate {
          animation: fadeInLeft 0.6s ease-out both;
        }

        .hand-char-visible {
          opacity: 1;
        }

        .hand-char-hidden {
          opacity: 0;
        }

        @keyframes blink {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 0.2; }
        }

        @keyframes slideDownFadeIn {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .subtitle-hidden {
          opacity: 0;
          transform: translateY(-20px);
        }
        .subtitle-reveal {
          animation: slideDownFadeIn 0.6s ease-out forwards;
        }
      `}</style>
    </>
  );
}